import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Button } from "antd";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../components/Breadcrumbs";
import SearchBox from "../../../components/SearchBox";
import DeviceModelPopup from "../../../components/modelpopup/DeviceModelPopup";
import moment from "moment";



const DeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener('click', handleClick);

    return () => {
      maximizeBtn.removeEventListener('click', handleClick);
      cleanup();
    };
  }, [isFullScreen]);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${apiUrl}/devices/devicelist`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDevices(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching devices:", error);
      toast.error("Failed to fetch devices. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiUrl && token) {
      fetchDevices();
    }
  }, [apiUrl, token]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDevices();
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  const handleSave = (device) => {
    alert("Device ID: " + device.id);

    if (isEditing) {
      setDevices((prevDevices) => {
        console.log("Previous Devices:", prevDevices);
        alert("Mapping devices");

        const updatedDevices = prevDevices.map((d) => {
          if (d.id === device.id) {
            alert(`Updating device ${d.id} to ${device.id}`);
            return device;
          }
          return d;
        });

        console.log("Updated Devices:", updatedDevices);
        return updatedDevices;
      });
    } else {
      setDevices((prevDevices) => {
        console.log("Adding new device to devices");
        const newDevices = [...prevDevices, device];
        console.log("New Devices:", newDevices);
        return newDevices;
      });
    }

    setIsEditing(false);
    setSelectedDevice(null);
  };

  const handleEditClick = (device) => {
    setSelectedDevice(device);
    setIsEditing(true);
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setSelectedDevice(null);
  };

  const handleDeleteClick = async (deviceId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this device!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${apiUrl}/devices/delete/${deviceId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setDevices((prevDevices) =>
          prevDevices.filter((device) => device.id !== deviceId)
        );

        toast.success("Device has been deleted.");
      } catch (error) {
        let errorMessage = "Failed to delete device. Please try again later.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  const updateDeviceStatus = async (deviceId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to change the status to ${newStatus === 1 ? 'Active' : 'Inactive'}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, change it!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axios.put(
          `${apiUrl}/devices/update/${deviceId}?isstatus=1`,
          { status: newStatus },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        setDevices(
          devices.map((device) =>
            device.id === deviceId
              ? { ...device, status: newStatus }
              : device
          )
        );

        await Swal.fire('Updated!', 'Device status has been updated.', 'success');
      } catch (error) {
        let errorMessage = 'Failed to update device status. Please try again later.';
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        await Swal.fire('Error!', errorMessage, 'error');
      }
    }
  };

  const columns = [
    {
      title: 'Device Serial Number',
      dataIndex: 'device_sr_no',
      key: 'device_sr_no',
      sorter: (a, b) => a.device_sr_no.localeCompare(b.device_sr_no),
    },
    {
      title: 'Description',
      dataIndex: 'device_desc',
      key: 'device_desc',
      sorter: (a, b) => a.device_desc.localeCompare(b.device_desc),
    },
    {
      title: 'Location',
      dataIndex: 'locationname',
      key: 'locationname',
      sorter: (a, b) => a.locationname - b.locationname,
    },
    {
      title: 'Date Added',
      dataIndex: 'date_added',
      key: 'date_added',
      render: (date) => moment(date).format("MMM DD, YYYY"),
      sorter: (a, b) => new Date(a.date_added) - new Date(b.date_added),
    },
    {
      title: 'Last Seen On',
      dataIndex: 'last_seen_on',
      key: 'last_seen_on',
      render: (date) => date ? moment(date).format("MMM DD, YYYY HH:mm") : 'N/A',
      sorter: (a, b) => new Date(a.last_seen_on) - new Date(b.last_seen_on),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const isActive = text === 1;
        const statusText = isActive ? 'Active' : 'Inactive';

        return (
          <div className="dropdown action-label">
            <Link
              className={`btn btn-white btn-sm btn-rounded dropdown-toggle ${isActive ? 'text-success' : 'text-danger'}`}
              to="#"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className={`far fa-dot-circle ${isActive ? 'text-success' : 'text-danger'}`} />{' '}
              {statusText}
            </Link>
            <div className="dropdown-menu">
              <Link
                className="dropdown-item"
                to="#"
                onClick={() => updateDeviceStatus(record.id, record.status)}
              >
                <i className={`far fa-dot-circle ${!isActive ? 'text-success' : 'text-danger'}`} />{' '}
                {!isActive ? 'Active' : 'Inactive'}
              </Link>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Online Status',
      dataIndex: 'onlineStatus',
      key: 'onlineStatus',
      render: (text) => (
        <span>
          <i className={`fa fa-circle ${text === 'online' ? 'text-success' : 'text-danger'}`} />
          {` ${text.charAt(0).toUpperCase() + text.slice(1)}`}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div className="dropdown dropdown-action text-center">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-center">
            <Link
              onClick={() => handleEditClick(record)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#add_device"
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <button
              className="dropdown-item"
              onClick={() => handleDeleteClick(record.id)}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
        <div className="page-header page-top-icons">
            <div className="row align-items-center">
              <div className="col-md-4">
                <h3 className="page-title">Devices</h3>
              </div>
              <div className="col-md-8 float-end ms-auto">
                <div className="d-flex title-head">
                  <div className="view-icons">
                    <Link to="#" className="grid-view btn btn-link" onClick={fetchDevices}><i className="las la-redo-alt" /></Link>
                    <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}><i className="las la-expand-arrows-alt" /></Link>
                  </div>
                  <Link to="#" className="btn add-btn" data-bs-toggle="modal" data-bs-target="#add_device" onClick={handleAddClick}>
                    <i className="la la-plus-circle" /> Add New
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {loading ? (
                <div id="loader-wrapper-">
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <SearchBox />
                  <Table
                    className="table-striped"
                    style={{ overflowX: "auto" }}
                    columns={columns}
                    dataSource={devices}
                    rowKey={(record) => record.id}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <DeviceModelPopup onSave={handleSave} deviceData={selectedDevice} isEditing={isEditing} />
    </>
  );
};

export default DeviceList;
